import { useEffect, useRef, useState } from "react";
import { Eraser, Pen, Trash2 } from "lucide-react";

const COLORS = ["#111827", "#dc2626", "#2563eb", "#16a34a", "#d97706"];

/** Small whiteboard: pen / eraser / colour / clear. `value` is a PNG data URL; onChange gets the new data URL after each stroke. */
export const SketchPad = ({ value, onChange, height = 220 }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  useEffect(() => {
    const c = ref.current, ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    if (value) { const img = new Image(); img.onload = () => ctx.drawImage(img, 0, 0); img.src = value; }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: (t.clientX - r.left) * (ref.current.width / r.width), y: (t.clientY - r.top) * (ref.current.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext("2d"), p = pos(e);
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = tool === "pen" ? color : "#fff"; ctx.lineWidth = tool === "pen" ? 2.5 : 18;
    ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault();
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current, ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div className="space-y-1" data-testid="note-sketch">
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => setTool("pen")} className={`h-7 w-7 rounded flex items-center justify-center ${tool === "pen" ? "bg-zinc-800 text-white" : "bg-zinc-100"}`} title="Pen" data-testid="note-sketch-pen"><Pen className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => setTool("eraser")} className={`h-7 w-7 rounded flex items-center justify-center ${tool === "eraser" ? "bg-zinc-800 text-white" : "bg-zinc-100"}`} title="Eraser" data-testid="note-sketch-eraser"><Eraser className="w-3.5 h-3.5" /></button>
        {COLORS.map((c) => <button key={c} type="button" onClick={() => { setColor(c); setTool("pen"); }} className={`h-5 w-5 rounded-full border-2 ${color === c && tool === "pen" ? "border-zinc-800" : "border-white"}`} style={{ background: c }} data-testid={`note-sketch-color-${c.slice(1)}`} />)}
        <button type="button" onClick={clear} className="ml-auto h-7 px-2 rounded bg-zinc-100 text-xs flex items-center gap-1" data-testid="note-sketch-clear"><Trash2 className="w-3 h-3" /> Clear</button>
      </div>
      <canvas ref={ref} width={640} height={height * 2} style={{ height }} className="w-full rounded border bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
    </div>
  );
};
